/**
 * User Menu component with Avatar
 * Dropdown menu for user actions: theme toggle, settings, logout
 */

import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useAuth } from '@/hooks/useAuth'
import { ThemeToggle } from '@/components/theme/ThemeToggle'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  IconSettings,
  IconLogout,
} from '@tabler/icons-react'

/**
 * UserMenu component with avatar and dropdown menu
 * Provides access to theme toggle, settings, and logout
 */
export function UserMenu() {
  const { user } = useAuth()
  const { logout } = useAuth0()

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin + '/login',
      },
    })
  }

  // Get user initials for avatar fallback
  const getInitials = () => {
    if (!user) return 'U'
    if (user.name) {
      const names = user.name.split(' ').filter(n => n.length > 0)
      if (names.length >= 2) {
        const first = names[0]?.[0]
        const last = names[names.length - 1]?.[0]
        if (first && last) {
          return `${first}${last}`.toUpperCase()
        }
      }
      if (user.name.length >= 2) {
        return user.name.substring(0, 2).toUpperCase()
      }
      return user.name.substring(0, 1).toUpperCase()
    }
    if (user.email && user.email.length >= 2) {
      return user.email.substring(0, 2).toUpperCase()
    }
    return 'U'
  }

  const userDisplayName = user?.name || user?.email || 'User'
  const userEmail = user?.email || ''

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all hover:opacity-80"
          aria-label="User menu"
        >
          <Avatar className="h-8 w-8 border-2 border-border">
            <AvatarImage src={user?.picture} alt={userDisplayName} />
            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{userDisplayName}</p>
            {userEmail && (
              <p className="text-xs leading-none text-muted-foreground">{userEmail}</p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Theme Toggle */}
        <div className="px-2 py-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Theme</span>
            <ThemeToggle />
          </div>
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Settings */}
        <DropdownMenuItem asChild>
          <Link to="/settings" className="flex items-center cursor-pointer">
            <IconSettings className="mr-2 h-4 w-4" stroke={1.5} />
            <span>Cài đặt</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Logout */}
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <IconLogout className="mr-2 h-4 w-4" stroke={1.5} />
          <span>Đăng xuất</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

